import React, { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Reusable full-screen chart shell (mobile-oriented). Renders children in a flex column
 * that fills the viewport with no page scroll; reports body size for dynamic plot height.
 *
 * Orientation: stays in the device’s current mode (portrait by default). We never call
 * screen.orientation.lock — if the user rotates the phone, resize/orientationchange only
 * recalculates layout; the UI is not forced to landscape.
 *
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {string} title
 * @param {React.ReactNode} children
 * @param {(size: { bodyWidth: number, bodyHeight: number }) => void} [onBodySizeChange]
 * @param {string} [overlayClassName]
 */
function ChartFullscreenModal({
  open,
  onClose,
  title,
  children,
  onBodySizeChange,
  overlayClassName = 'z-[11050]',
}) {
  const titleId = useId();
  const bodyRef = useRef(null);

  const reportBodySize = useCallback(() => {
    const el = bodyRef.current;
    if (!el || typeof onBodySizeChange !== 'function') return;
    const rect = el.getBoundingClientRect();
    const bodyWidth = Math.floor(rect.width);
    const bodyHeight = Math.floor(rect.height);
    if (bodyWidth > 0 && bodyHeight > 0) {
      onBodySizeChange({ bodyWidth, bodyHeight });
    }
  }, [onBodySizeChange]);

  useLayoutEffect(() => {
    if (!open) return undefined;
    reportBodySize();
    const el = bodyRef.current;
    if (!el) return undefined;

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => reportBodySize());
      observer.observe(el);
    }
    window.addEventListener('resize', reportBodySize);
    window.addEventListener('orientationchange', reportBodySize);
    const t = window.setTimeout(reportBodySize, 50);

    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', reportBodySize);
      window.removeEventListener('orientationchange', reportBodySize);
      window.clearTimeout(t);
    };
  }, [open, reportBodySize]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className={`fixed inset-0 flex flex-col bg-white transition-opacity duration-200 ${overlayClassName}`}
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
    >
      <header className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 px-3 py-2.5">
        <h2 id={titleId} className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
          {title}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          aria-label="Close full screen"
        >
          <X className="h-5 w-5" aria-hidden />
        </button>
      </header>
      <div
        ref={bodyRef}
        className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-1"
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

export default React.memo(ChartFullscreenModal);
